import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Gavel, Plus, Trash2, Target } from "lucide-react";
import { decisionTypeColors } from "../../lib/decisions";

export const ExpectedDecisionsSection = ({ decisions = [], onDecisionsChange }) => {
  const { t } = useTranslation("meeting");

  const decisionTypeLabels = useMemo(
    () => ({
      strategy:   t("decType_strategy"),
      finance:    t("decType_finance"),
      governance: t("decType_governance"),
      operations: t("decType_operations"),
      personnel:  t("decType_personnel"),
      compliance: t("decType_compliance"),
    }),
    [t]
  );

  const expectedOutcomeLabels = useMemo(
    () => ({
      decision:    t("expOutcome_decision"),
      resolution:  t("expOutcome_resolution"),
      approval:    t("expOutcome_approval"),
      discussion:  t("expOutcome_discussion"),
      information: t("expOutcome_information"),
    }),
    [t]
  );

  const [newDec, setNewDec] = useState({
    title:           "",
    decisionType:    "strategy",
    expectedOutcome: "decision",
    description:     "",
  });

  const handleAdd = () => {
    if (!newDec.title.trim()) return;
    onDecisionsChange([
      ...decisions,
      {
        id:              `exp-${Date.now()}`,
        title:           newDec.title,
        decisionType:    newDec.decisionType,
        expectedOutcome: newDec.expectedOutcome,
        description:     newDec.description || undefined,
      },
    ]);
    setNewDec({ title: "", decisionType: "strategy", expectedOutcome: "decision", description: "" });
  };

  const handleRemove = (id) => {
    onDecisionsChange(decisions.filter((d) => d.id !== id));
  };

  return (
    <div>
      <Label className="flex items-center gap-2 mb-3">
        <Gavel className="w-4 h-4" />
        {t("expectedBoardDecisions")}
      </Label>
      <p className="text-xs text-muted-foreground mb-3">
        {t("expectedBoardDecisionsHint")}
      </p>

      {decisions.length > 0 && (
        <div className="space-y-2 mb-4">
          {decisions.map((dec) => (
            <div key={dec.id} className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg border">
              <Target className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm">{dec.title}</p>
                  <Badge className={`${decisionTypeColors[dec.decisionType] || ""} text-xs`}>
                    {decisionTypeLabels[dec.decisionType] || dec.decisionType}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {expectedOutcomeLabels[dec.expectedOutcome] || dec.expectedOutcome}
                  </Badge>
                </div>
                {dec.description && (
                  <p className="text-xs text-muted-foreground mt-1">{dec.description}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleRemove(dec.id)}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3 p-3 bg-muted/20 rounded-lg border border-dashed">
        <Input
          placeholder={t("decisionTitleExamplePh")}
          value={newDec.title}
          onChange={(e) => setNewDec((p) => ({ ...p, title: e.target.value }))}
        />
        <div className="grid grid-cols-2 gap-2">
          <Select
            value={newDec.decisionType}
            onValueChange={(v) => setNewDec((p) => ({ ...p, decisionType: v }))}
          >
            <SelectTrigger className="h-9"><SelectValue placeholder={t("decisionType")} /></SelectTrigger>
            <SelectContent>
              {Object.entries(decisionTypeLabels).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={newDec.expectedOutcome}
            onValueChange={(v) => setNewDec((p) => ({ ...p, expectedOutcome: v }))}
          >
            <SelectTrigger className="h-9"><SelectValue placeholder={t("expectedOutcome")} /></SelectTrigger>
            <SelectContent>
              {Object.entries(expectedOutcomeLabels).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Textarea
          placeholder={t("descriptionOptional")}
          className="min-h-[60px]"
          value={newDec.description}
          onChange={(e) => setNewDec((p) => ({ ...p, description: e.target.value }))}
        />
        <Button variant="outline" size="sm" onClick={handleAdd} className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          {t("addExpectedDecision")}
        </Button>
      </div>
    </div>
  );
};

export default ExpectedDecisionsSection;
